import { useState, useEffect, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SqlEditor } from '@/components/ui/SqlEditor';
import { Eye, ChevronDown, ChevronUp, Columns } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet } from '@/api';
import { useAuth } from '@/context/useAuth';
import { DataPreviewModal } from '@/components/common/DataPreviewModal';
import { Button } from '@/components/ui/button';

interface SourceNodeConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

// ─── Reusable Column Selector ───────────────────────────────────────────────
interface ColumnSelectorProps {
    allColumns: { name: string; type?: string }[];
    selectedColumns: string[];     // [] means "all selected"
    onChange: (cols: string[]) => void;
}

const ColumnSelector = ({ allColumns, selectedColumns, onChange }: ColumnSelectorProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const effectiveSelected: string[] = selectedColumns.length === 0
        ? allColumns.map(c => c.name)
        : selectedColumns;

    const filtered = useMemo(() =>
        allColumns.filter(c => c.name.toLowerCase().includes(search.toLowerCase())),
        [allColumns, search]
    );

    const isAllSelected = effectiveSelected.length === allColumns.length;
    const selectedCount = effectiveSelected.length;

    const toggle = (name: string) => {
        const next = effectiveSelected.includes(name)
            ? effectiveSelected.filter(c => c !== name)
            : [...effectiveSelected, name];
        // If all selected, store [] to signal "all"
        onChange(next.length === allColumns.length ? [] : next);
    };

    const selectAll = () => onChange([]);
    const clearAll = () => {
        // keep at least one if trying to clear all
        if (allColumns.length > 0) onChange([allColumns[0].name]);
    };

    if (allColumns.length === 0) return null;

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Header / trigger */}
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
                <div className="flex items-center gap-2">
                    <Columns className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                    <span className="text-[11px] font-semibold text-gray-700">Select Columns</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isAllSelected ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>
                        {isAllSelected ? 'All' : `${selectedCount} / ${allColumns.length}`}
                    </span>
                </div>
                {open ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
            </button>

            {open && (
                <div className="bg-white">
                    {/* Search + bulk actions */}
                    <div className="px-3 pt-2 pb-1 border-b border-gray-100 flex items-center gap-2">
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search columns…"
                            className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:border-indigo-400"
                        />
                        <button type="button" onClick={selectAll} className="text-[10px] text-indigo-600 hover:underline whitespace-nowrap">All</button>
                        <button type="button" onClick={clearAll} className="text-[10px] text-gray-400 hover:text-gray-600 whitespace-nowrap">Clear</button>
                    </div>

                    {/* Column list */}
                    <div className="max-h-44 overflow-y-auto p-2 space-y-0.5">
                        {filtered.map(col => {
                            const checked = effectiveSelected.includes(col.name);
                            return (
                                <label
                                    key={col.name}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer group"
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(col.name)}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                                    />
                                    <span className="text-xs text-gray-700 flex-1 truncate" title={col.name}>{col.name}</span>
                                    {col.type && (
                                        <span className="text-[9px] text-gray-400 bg-gray-100 px-1 rounded shrink-0">{col.type}</span>
                                    )}
                                </label>
                            );
                        })}
                        {filtered.length === 0 && (
                            <p className="text-[10px] text-gray-400 italic px-2 py-2">No columns match "{search}"</p>
                        )}
                    </div>

                    <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50">
                        <p className="text-[10px] text-indigo-600 font-medium">
                            {selectedCount} column{selectedCount !== 1 ? 's' : ''} selected → will flow downstream
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
export const SourceNodeConfig = ({ node, onChangeData }: SourceNodeConfigProps) => {
    const { token } = useAuth();
    const data = node.data || {};
    const [datasets, setDatasets] = useState<any[]>([]);
    const [datasources, setDatasources] = useState<any[]>([]);
    const [tables, setTables] = useState<any[]>([]);
    const [loadingDatasets, setLoadingDatasets] = useState(false);
    const [loadingDatasources, setLoadingDatasources] = useState(false);
    const [loadingTables, setLoadingTables] = useState(false);

    // Data Preview State
    const [previewModal, setPreviewModal] = useState({ isOpen: false, dataSourceId: '', objectName: '', query: '' });

    // Fetch Data Models on mount
    const fetchDataModels = async () => {
        setLoadingDatasets(true);
        try {
            const res = await apiGet('/datamodels', token);
            if (res) setDatasets(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error('Failed to fetch data models', e);
        } finally {
            setLoadingDatasets(false);
        }
    };

    useEffect(() => {
        if (!token) return;
        fetchDataModels();
    }, [token]);

    // Fetch datasources on mount
    useEffect(() => {
        if (!token) return;
        const fetchDatasources = async () => {
            setLoadingDatasources(true);
            try {
                const res = await apiGet('/datasources', token);
                if (res) setDatasources(Array.isArray(res) ? res : []);
            } catch (e) {
                console.error('Failed to fetch datasources', e);
            } finally {
                setLoadingDatasources(false);
            }
        };
        fetchDatasources();
    }, [token]);

    // Fetch tables when datasource is selected in 'reuse' mode
    useEffect(() => {
        const isReuseMode = !data.connectionMode || data.connectionMode === 'reuse';
        if (!data.dataSourceId || !isReuseMode || !token) return;

        const fetchTables = async () => {
            setLoadingTables(true);
            try {
                const structure = await apiGet(`/datasources/${data.dataSourceId}/structure`, token);
                if (structure) setTables(structure.tables || []);
            } catch (e) {
                console.error('[SourceNodeConfig] Exception fetching tables:', e);
                setTables([]);
            } finally {
                setLoadingTables(false);
            }
        };
        fetchTables();
    }, [data.dataSourceId, data.connectionMode, token]);

    const handleConnectionModeChange = (mode: string) => {
        // Clear all connection/model related fields when switching modes
        onChangeData({
            connectionMode: mode,
            dataSourceId: '',
            table: '',
            tables: [],
            columns: [],
            outputSchema: [],
            selectedColumns: [],
            isCustomSql: false,
            customSql: '',
            dataModelId: '',
            datasetId: '',
            isDatasetSql: false,
            datasetSql: '',
        });
        setTables([]);
    };

    // Helper to get all columns available from selected model tables
    const getSelectedModelTables = () => {
        const id = data.dataModelId || data.datasetId;
        const model = datasets.find(d => d.id === id);
        return model?.tables || [];
    };

    // Build the full column list from selected tables (Data Model mode)
    const modelAllColumns = useMemo((): { name: string; type: string }[] => {
        const selectedTables: string[] = Array.isArray(data.tables) ? data.tables : (data.table ? [data.table] : []);
        if (selectedTables.length === 0) return [];
        const modelTables = getSelectedModelTables();
        const seen = new Set<string>();
        const cols: { name: string; type: string }[] = [];
        selectedTables.forEach(tblName => {
            const tbl = modelTables.find((m: any) => m.name === tblName);
            if (tbl && Array.isArray(tbl.columns)) {
                tbl.columns.forEach((c: any) => {
                    const baseName = c.name;
                    const colName = seen.has(baseName) ? `${tblName}_${baseName}` : baseName;
                    seen.add(baseName);
                    cols.push({ name: colName, type: c.data_type || 'string' });
                });
            }
        });
        return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.tables, data.table, data.dataModelId, data.datasetId, datasets]);

    // Columns for Data Source / Select Table mode
    const dsAllColumns: { name: string; type: string }[] = useMemo(() => {
        return Array.isArray(data.columns) ? data.columns : [];
    }, [data.columns]);

    // Apply column selection and update outputSchema
    const handleColumnSelection = (cols: string[], allCols: { name: string; type: string }[]) => {
        const effective = cols.length === 0 ? allCols : allCols.filter(c => cols.includes(c.name));
        onChangeData({ selectedColumns: cols, outputSchema: effective });
    };

    return (
        <div className="space-y-6">
            {/* Connection Mode Toggle */}
            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Source Configuration</label>
                <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                    {['reuse', 'dataset'].map(mode => (
                        <button
                            key={mode}
                            type="button"
                            className={`flex-1 text-[10px] py-1.5 rounded-md font-medium capitalize transition-all ${(
                                data.connectionMode === mode ||
                                (!data.connectionMode && mode === (data.dataModelId || data.datasetId ? 'dataset' : 'reuse'))
                            )
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => handleConnectionModeChange(mode)}
                        >
                            {mode === 'dataset' ? 'Data Models' : 'Data Source'}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── DATA MODEL MODE ── */}
            {(!data.connectionMode || data.connectionMode === 'dataset') && (
                <div className={`space-y-4 ${(!data.connectionMode && !data.datasetId && !data.dataModelId) ? 'hidden' : ''}`}>
                    {/* Data Model Selector */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] font-medium text-gray-500">Select Data Model</label>
                            <button
                                type="button"
                                onClick={fetchDataModels}
                                disabled={loadingDatasets}
                                title="Refresh data models list"
                                className="text-[10px] text-indigo-500 hover:text-indigo-700 disabled:opacity-40 flex items-center gap-1"
                            >
                                <span className={loadingDatasets ? 'animate-spin inline-block' : ''}>↻</span>
                                {loadingDatasets ? 'Loading…' : 'Refresh'}
                            </button>
                        </div>
                        <Select
                            value={data.dataModelId || data.datasetId || ''}
                            onValueChange={(v) => {
                                const model = datasets.find(d => d.id === v);
                                const updates: any = {
                                    dataModelId: v,
                                    datasetId: v,
                                };
                                if (model) {
                                    updates.label = model.name;
                                    updates.table = '';
                                    updates.tables = [];
                                    updates.columns = [];
                                    updates.outputSchema = [];
                                    updates.selectedColumns = [];
                                }
                                onChangeData(updates);
                            }}
                        >
                            <SelectTrigger className="w-full text-sm px-3 py-2">
                                <SelectValue placeholder={loadingDatasets ? 'Loading...' : 'Select a Data Model...'} />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                {datasets.map((ds) => (
                                    <SelectItem key={ds.id} value={ds.id}>{ds.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Table Selector OR Custom SQL — if model selected */}
                    {data.dataModelId && (
                        <div className="space-y-4">
                            {/* Query Mode Toggle */}
                            <div className="space-y-2">
                                <label className="text-[11px] font-medium text-gray-500">Query Mode</label>
                                <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                                    <button
                                        type="button"
                                        className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${!data.isDatasetSql
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        onClick={() => onChangeData({ isDatasetSql: false, datasetSql: '' })}
                                    >
                                        Auto Join Tables
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${data.isDatasetSql
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        onClick={() => onChangeData({ isDatasetSql: true, tables: [], table: '', columns: [], outputSchema: [], selectedColumns: [] })}
                                    >
                                        Custom SQL
                                    </button>
                                </div>
                            </div>

                            {data.isDatasetSql ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between pointer-events-none">
                                        <label className="text-[11px] font-medium text-gray-500">Custom SQL Query</label>
                                    </div>
                                    <SqlEditor
                                        value={data.datasetSql || ''}
                                        onChange={(val) => onChangeData({ datasetSql: val })}
                                        className="border-gray-200"
                                    />
                                    <p className="text-[10px] text-gray-400">
                                        Write raw SQL. Tables are registered as views. <br />
                                        Example: <code className="bg-gray-100 px-1 rounded">SELECT a.*, c.CalendarYear FROM DimAdjustment a JOIN DimCalendar c ON a.DateKey = c.DateKey</code>
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Table multi-select */}
                                    <div className="space-y-2 border border-gray-100 rounded-lg p-3 bg-white">
                                        <label className="text-[11px] font-medium text-gray-500 block mb-1">Select Tables</label>
                                        <p className="text-[10px] text-gray-500 mb-2">Select one or more tables to include.</p>
                                        {/* Select All row */}
                                        <label className="flex items-center space-x-2 py-1.5 px-2 mb-2 rounded hover:bg-gray-100 cursor-pointer border border-gray-200">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                checked={getSelectedModelTables().length > 0 && Array.isArray(data.tables) && data.tables.length === getSelectedModelTables().length}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    const modelTables = getSelectedModelTables();
                                                    if (isChecked && modelTables.length > 0) {
                                                        const next = modelTables.map((t: any) => t.name);
                                                        const seen = new Set<string>();
                                                        const allCols: { name: string; type: string }[] = [];
                                                        next.forEach((tblName: string) => {
                                                            const tbl = modelTables.find((m: any) => m.name === tblName);
                                                            if (tbl && Array.isArray(tbl.columns)) {
                                                                tbl.columns.forEach((c: any) => {
                                                                    const baseName = c.name;
                                                                    const colName = seen.has(baseName) ? `${tblName}_${baseName}` : baseName;
                                                                    seen.add(baseName);
                                                                    allCols.push({ name: colName, type: c.data_type || 'string' });
                                                                });
                                                            }
                                                        });
                                                        onChangeData({ tables: next, table: next[0] || '', columns: allCols, outputSchema: allCols, selectedColumns: [] });
                                                        toast.success(`Selected all ${next.length} tables`);
                                                    } else {
                                                        onChangeData({ tables: [], table: '', columns: [], outputSchema: [], selectedColumns: [] });
                                                        toast.success('Cleared tables');
                                                    }
                                                }}
                                            />
                                            <span className="text-sm font-semibold text-gray-700">Select All</span>
                                        </label>
                                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                                            {getSelectedModelTables().map((t: any) => {
                                                const selectedTables = Array.isArray(data.tables) ? data.tables : (data.table ? [data.table] : []);
                                                const isChecked = selectedTables.includes(t.name);
                                                return (
                                                    <div
                                                        key={t.name}
                                                        className="flex items-center space-x-2 py-1.5 px-2 rounded hover:bg-gray-100 cursor-pointer"
                                                        onClick={() => {
                                                            const current = Array.isArray(data.tables) ? [...data.tables] : (data.table ? [data.table] : []);
                                                            const next = isChecked
                                                                ? current.filter((x: string) => x !== t.name)
                                                                : [...current, t.name];
                                                            const modelTables = getSelectedModelTables();
                                                            const seen = new Set<string>();
                                                            const allCols: { name: string; type: string }[] = [];
                                                            next.forEach((tblName: string) => {
                                                                const tbl = modelTables.find((m: any) => m.name === tblName);
                                                                if (tbl && Array.isArray(tbl.columns)) {
                                                                    tbl.columns.forEach((c: any) => {
                                                                        const baseName = c.name;
                                                                        const dataType = c.data_type || 'string';
                                                                        let colName = baseName;
                                                                        if (seen.has(baseName)) colName = `${tblName}_${baseName}`;
                                                                        else seen.add(baseName);
                                                                        allCols.push({ name: colName, type: dataType });
                                                                    });
                                                                }
                                                            });
                                                            onChangeData({
                                                                tables: next,
                                                                table: next[0] || '',
                                                                columns: allCols,
                                                                outputSchema: allCols,
                                                                selectedColumns: [],   // reset column selection when tables change
                                                            });
                                                            toast.success(next.length ? `Loaded ${allCols.length} columns from ${next.length} table(s)` : 'Cleared tables');
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                            checked={isChecked}
                                                            onChange={() => { }}
                                                        />
                                                        <span className="text-sm text-gray-700">{t.name}</span>
                                                        {t.columns && <span className="text-[10px] text-gray-400">({t.columns.length} cols)</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Column selector — only when at least one table is chosen */}
                                    {modelAllColumns.length > 0 && (
                                        <ColumnSelector
                                            allColumns={modelAllColumns}
                                            selectedColumns={Array.isArray(data.selectedColumns) ? data.selectedColumns : []}
                                            onChange={(cols) => handleColumnSelection(cols, modelAllColumns)}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── DATA SOURCE (REUSE) MODE ── */}
            {(!data.connectionMode || data.connectionMode === 'reuse') && (
                <div className={`space-y-4 ${data.connectionMode === 'dataset' ? 'hidden' : ''}`}>
                    {/* Connection Selector */}
                    <div className="space-y-2">
                        <label className="text-[11px] font-medium text-gray-500">Select Connection</label>
                        <Select
                            value={data.dataSourceId || ''}
                            onValueChange={(v) => {
                                onChangeData({ dataSourceId: v, table: '', columns: [], outputSchema: [], selectedColumns: [] });
                                setTables([]);
                                setLoadingTables(true);
                                apiGet(`/datasources/${v}/structure`)
                                    .then(s => setTables(s?.tables || []))
                                    .catch(() => setTables([]))
                                    .finally(() => setLoadingTables(false));
                            }}
                        >
                            <SelectTrigger className="w-full text-sm px-3 py-2">
                                <SelectValue placeholder={loadingDatasources ? 'Loading Connections...' : 'Select a Connection...'} />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                {datasources.map((conn) => (
                                    <SelectItem key={conn.id} value={conn.id}>
                                        {conn.name} ({conn.type})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Query Mode Toggle: Table vs Custom SQL */}
                    <div className="space-y-2">
                        <label className="text-[11px] font-medium text-gray-500">Query Mode</label>
                        <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                            <button
                                type="button"
                                className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${!data.isCustomSql
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                onClick={() => onChangeData({ isCustomSql: false, customSql: '', selectedColumns: [] })}
                            >
                                Select Table
                            </button>
                            <button
                                type="button"
                                className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${data.isCustomSql
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                onClick={() => onChangeData({ isCustomSql: true, table: '', columns: [], outputSchema: [], selectedColumns: [] })}
                            >
                                Custom SQL
                            </button>
                        </div>
                    </div>

                    {/* Custom SQL Editor OR Table Selector */}
                    {data.isCustomSql ? (
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <label className="text-[11px] font-medium text-gray-500">SQL Query</label>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 gap-1 text-[10px]"
                                    onClick={() => setPreviewModal({ isOpen: true, dataSourceId: data.dataSourceId, objectName: '', query: data.customSql })}
                                    disabled={!data.customSql}
                                >
                                    <Eye className="h-3 w-3" /> Preview
                                </Button>
                            </div>
                            <SqlEditor
                                value={data.customSql || ''}
                                onChange={(val) => onChangeData({ customSql: val })}
                                className="border-gray-200"
                            />
                            <p className="text-[10px] text-gray-400">Write your own query instead of selecting a table.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* Table dropdown */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <label className="text-[11px] font-medium text-gray-500">Table</label>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 gap-1 text-[10px]"
                                        onClick={() => setPreviewModal({ isOpen: true, dataSourceId: data.dataSourceId, objectName: data.table, query: '' })}
                                        disabled={!data.table}
                                    >
                                        <Eye className="h-3 w-3" /> Preview
                                    </Button>
                                </div>
                                <Select
                                    value={data.table || ''}
                                    onValueChange={async (v) => {
                                        // Reset columns and selectedColumns when table changes
                                        onChangeData({ table: v, columns: [], outputSchema: [], selectedColumns: [] });
                                        if (data.dataSourceId && v) {
                                            try {
                                                const schema = await apiGet(`/datasources/${data.dataSourceId}/schema`);
                                                if (Array.isArray(schema)) {
                                                    const tbl = schema.find((t: any) => t.name === v);
                                                    if (tbl && Array.isArray(tbl.columns)) {
                                                        onChangeData({ columns: tbl.columns, outputSchema: tbl.columns, selectedColumns: [] });
                                                        toast.success(`Loaded ${tbl.columns.length} columns from ${v}`);
                                                    }
                                                }
                                            } catch (e) { console.error('Failed to auto-fetch columns', e); }
                                        }
                                    }}
                                    disabled={!data.dataSourceId || loadingTables}
                                >
                                    <SelectTrigger className="w-full text-sm px-3 py-2">
                                        <SelectValue placeholder={loadingTables ? 'Loading tables...' : (tables.length > 0 ? 'Select Table...' : 'No tables found')} />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                        {tables.map((t) => (
                                            <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Column selector — only when a table + its columns are loaded */}
                            {data.table && dsAllColumns.length > 0 && (
                                <ColumnSelector
                                    allColumns={dsAllColumns}
                                    selectedColumns={Array.isArray(data.selectedColumns) ? data.selectedColumns : []}
                                    onChange={(cols) => handleColumnSelection(cols, dsAllColumns)}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Data Preview Modal */}
            <DataPreviewModal
                isOpen={previewModal.isOpen}
                onClose={() => setPreviewModal({ ...previewModal, isOpen: false })}
                dataSourceId={previewModal.dataSourceId || ''}
                objectName={previewModal.objectName || ''}
                query={previewModal.query || ''}
            />
        </div>
    );
};

export default SourceNodeConfig;
